import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { gradientHero } from '../../theme';
import PublicNavBar from '../../components/PublicNavBar';
import PortalFooter from '../../components/PortalFooter';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa', display: 'flex', flexDirection: 'column' }}>
      <PublicNavBar />

      <Box sx={{ background: gradientHero, color: '#fff', py: 6, textAlign: 'center' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Willkommen zurück
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Melden Sie sich an, um auf Ihr Konto zuzugreifen
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="sm">
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          minHeight="50vh"
        >
          <Paper elevation={0} sx={{ p: 5, backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}>
            <Typography variant="h5" component="h2" mb={4} textAlign="center" sx={{ fontWeight: 'bold' }}>
              Anmelden
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="E-Mail-Adresse"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                disabled={isLoading}
                placeholder="admin@example.com"
                variant="outlined"
              />

              <TextField
                fullWidth
                label="Passwort"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                disabled={isLoading}
                variant="outlined"
              />

              <Button
                fullWidth
                variant="contained"
                type="submit"
                sx={{ mt: 4 }}
                disabled={isLoading}
                size="large"
              >
                {isLoading ? <CircularProgress size={24} /> : 'Anmelden'}
              </Button>
            </form>

            <Stack spacing={2} sx={{ mt: 4 }}>
              <Typography textAlign="center" color="textSecondary">
                <Link to="/forgot-password" style={{ textDecoration: 'none', color: '#667eea' }}>
                  Passwort vergessen?
                </Link>
              </Typography>
              <Typography textAlign="center" color="textSecondary">
                Noch kein Konto?{' '}
                <Link to="/register" style={{ textDecoration: 'none', color: '#667eea', fontWeight: 600 }}>
                  Hier registrieren
                </Link>
              </Typography>
            </Stack>
          </Paper>
        </Box>
      </Container>

      <PortalFooter />
    </Box>
  );
};

export default LoginPage;
